/**
 * Technician-facing workshop API surface — pure endpoint definitions. No
 * React, no react-query; the hooks in `useWorkshop.ts` layer on top and
 * components consume only the FE shapes from `./types.ts`.
 *
 *   GET  /technical/do-list                     → DoListResponse (Today home)
 *   GET  /technical/work-orders/{id}/detail     → WorkOrderDetail (detail tree)
 *   POST /technical/work-orders/{id}/claim      → WorkOrderResponse (the whole job; 409 when nothing is loose)
 *   POST /technical/work-orders/{id}/services/{serviceId}/claim → ServiceResponse
 *        (ONE loose service — only once the caller already holds a stake
 *        elsewhere on the job; see hasStake in todayGroups.ts / woDetail.ts.
 *        409 when the service isn't OPEN and unassigned any more.)
 *   POST /technical/work-orders/{id}/diagnose   → WorkOrderResponse (job-level note)
 *   POST /technical/services/{id}/labour/start  → ServiceResponse (claimed/paused → in_progress)
 *   POST /technical/services/{id}/labour/pause  → ServiceResponse (in_progress → paused)
 *
 * Note: GET /technical/work-orders/{id} (no /detail) is a DIFFERENT,
 * flat response (WorkOrderResponse) carrying the WO-level note timeline
 * — unrelated to the tree shape below, not used by this app.
 *
 * The unread-note badge's read markers (GET/PUT
 * /technical/work-orders/{,{id}/notes/read}) do not go through this
 * module — `features/work-order/notesSeenStore.ts` calls them directly
 * through `@vollgas/shared/hooks`'s `createNotesSeenStore`, the same way
 * every app with the badge does.
 */
import { api, logout as sharedLogout, logoutEverywhereElse } from './apiClient'
import type {
  AuthUser,
  DoListResponse,
  LabourEntryResponse,
  LoginRequest,
  ManualLabourRequest,
  ProposeServiceRequest,
  ServiceResponse,
  WorkOrderDetail,
  WorkOrderResponse,
} from './types'

const enc = encodeURIComponent

export const workshopApi = {
  /** Today home feed (WO → service). */
  getDoList: () => api.get<DoListResponse>('/technical/do-list'),

  /**
   * Every work order assigned to the caller — the board's history and
   * its future, which the do-list does not carry.
   *
   * `/active` is a misnomer: `findActiveByTechnicianId` filters on the
   * technician and `deletedAt IS NULL` only, with NO status predicate,
   * so COMPLETED and CANCELLED jobs come back too, ordered `openedAt`
   * DESC. That is what lets DONE mean "everything, recent first" and
   * UPCOMING mean "assigned, scheduled later" with no backend change.
   *
   * Flat `WorkOrderResponse` — no service/task tree — so anything built
   * on it renders a leaner card than the do-list's.
   */
  getMyWorkOrders: () => api.get<WorkOrderResponse[]>('/technical/work-orders/active'),

  /** The WO detail tree (WO → service → task). */
  getWorkOrderDetail: (id: string) =>
    api.get<WorkOrderDetail>(`/technical/work-orders/${enc(id)}/detail`),

  /**
   * Take the WHOLE job off the floor: every loose (agreed, unassigned)
   * service becomes the caller's, and so does the job itself when nobody
   * owns it yet. The one claim this app offers — a job is worked as its
   * services, not as steps. 409 when nothing on it is left to take.
   */
  claimWorkOrder: (workOrderId: string) =>
    api.post<WorkOrderResponse>(`/technical/work-orders/${enc(workOrderId)}/claim`),

  /**
   * Take ONE loose service — the per-service counterpart to `claimWorkOrder`,
   * offered once the caller already holds a stake elsewhere on the job (see
   * `hasStake`). 409 when the service isn't OPEN and unassigned any more.
   */
  claimService: (workOrderId: string, serviceId: string) =>
    api.post<ServiceResponse>(
      `/technical/work-orders/${enc(workOrderId)}/services/${enc(serviceId)}/claim`),

  /** Start (or resume) the caller's own timer on a claimed service. */
  startServiceLabour: (serviceId: string) =>
    api.post<ServiceResponse>(`/technical/services/${enc(serviceId)}/labour/start`),

  /** Pause the caller's own running timer on a service. */
  pauseServiceLabour: (serviceId: string) =>
    api.post<ServiceResponse>(`/technical/services/${enc(serviceId)}/labour/pause`),

  /** Manually log a time span on a service — lands in the SAME labour total
   *  as the live timer (the server stamps the segments with the serviceId). */
  addServiceManualLabour: (serviceId: string, body: ManualLabourRequest) =>
    api.post<ReadonlyArray<LabourEntryResponse>>(
      `/technical/services/${enc(serviceId)}/labour/manual`, body),

  /** Mark my service finished — stamps `completedAt` (V0227). */
  completeService: (serviceId: string) =>
    api.post<ServiceResponse>(`/technical/services/${enc(serviceId)}/complete`),

  /** Finish the whole WO (assignee-only; 409 if the WO has no labour/parts). */
  completeWorkOrder: (workOrderId: string, mileageAtComplete?: number) =>
    api.post<void>(`/technical/work-orders/${enc(workOrderId)}/complete`,
      mileageAtComplete == null ? {} : { mileageAtComplete }),

  /** Propose additional work discovered mid-job — creates a PENDING_APPROVAL
   *  SERVICE, checklist expanded, that the manager clears with the customer. */
  proposeService: (workOrderId: string, body: ProposeServiceRequest) =>
    api.post<ServiceResponse>(
      `/technical/work-orders/${enc(workOrderId)}/services/propose`, body),

  // --- Job-level notes (wo_notes) ------------------------------------------
  // The manager's intake note lives here, and so does anything the crew
  // writes about the job as a whole. `diagnose` is the backend's name for
  // "append a note to the job"; the response is the WO with its timeline.
  addWorkOrderNote: (workOrderId: string, body: string) =>
    api.post<WorkOrderResponse>(`/technical/work-orders/${enc(workOrderId)}/diagnose`, { note: body }),
}

// ============================================================================
// Identity / auth — the technician app's single user surface. If more apps
// need it, hoist this block to `packages/shared`.
// ============================================================================

export const authApi = {
  // `skipRefresh`: a 401 here means there is no live session, and
  // `/users/auth/refresh` cannot rescue it — both hang off the same
  // `SessionAuthFilter` principal, and refresh only *slides* a live
  // session. Retrying it costs the anonymous cold load a guaranteed-401
  // round-trip and a second red line in the console, every page load.
  me: () => api.get<AuthUser>('/users/me', { skipRefresh: true }),
  login: (body: LoginRequest) => api.post<AuthUser>('/users/auth/login', body),
  // Shared `logout` retries once on a stale CSRF token and throws if the
  // session genuinely survives — callers must surface that, not swallow it.
  logout: () => sharedLogout(),
  logoutEverywhereElse: () => logoutEverywhereElse(),
  refresh: () => api.post<AuthUser>('/users/auth/refresh'),
}
